import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  Router,
  Routes,
} from '@angular/router';
import { inject } from '@angular/core';
import { MdcInventoryService } from './services/inventory.service';
import {
  DefaultMdcInventoryStrategy,
  MDC_INVENTORY_STRATEGY,
  MdcInventoryConfig,
} from './mdc-inventory.tokens';
import { InventoryConfigService } from './services/inventory-config.service';
import { RemoteConfigResolver } from '@softline/application';
import { InventoryDetailStrategy } from './pages/inventory-detail/strategies/inventory-detail.strategy';
import { InventoryDetailCreateStrategy } from './pages/inventory-detail/strategies/inventory-detail-create.strategy';
import { InventoryDetailEditStrategy } from './pages/inventory-detail/strategies/inventory-detail-edit.strategy';

const canActivateInventory: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const inventoryService = inject(MdcInventoryService);
  const inventoryId = route.paramMap.get('id');

  function redirectToSetup() {
    const urlTree = router.createUrlTree(['/mdc-inventory/setup/storage-location']);
    return new RedirectCommand(urlTree, {
      skipLocationChange: true,
      onSameUrlNavigation: 'ignore',
    });
  }

  if (!inventoryId || isNaN(+inventoryId)) {
    return redirectToSetup();
  }

  const inventory = await inventoryService.loadById(+inventoryId);

  if (!inventory) {
    return redirectToSetup();
  }

  return true;
};

export const mdcInventoryRouteFactory = (config: MdcInventoryConfig): Routes => {
  return [
    {
      path: 'mdc-inventory',
      providers: [InventoryConfigService],
      children: [
        {
          path: 'setup',
          providers: [
            {
              provide: MDC_INVENTORY_STRATEGY,
              useFactory: () => {
                return config?.strategy ?? new DefaultMdcInventoryStrategy();
              },
            },
          ],
          children: [
            {
              path: 'storage-location',
              loadComponent: () => import('./pages/storage-location/storage-location'),
            },
            {
              path: ':stockId/set-date',
              loadComponent: () => import('./pages/set-date/set-date'),
            },
            {
              path: '',
              redirectTo: '/mdc-inventory/setup/storage-location',
              pathMatch: 'full',
            },
          ],
        },
        {
          path: 'storage/:stockId/inventory/:id',
          canActivate: [canActivateInventory],
          children: [
            {
              path: '',
              pathMatch: 'full',
              loadComponent: () => import('./pages/inventory/inventory'),
            },
            {
              path: 'pallet-scan/:palletId',
              loadComponent: () =>
                import('./pages/pallet-scan-result-list/pallet-scan-result-list'),
            },
            {
              path: 'storage-location-scan/:storageLocationId',
              loadComponent: () =>
                import('./pages/storage-location-result-list/storage-location-result-list'),
            },
            {
              path: 'products/:productId',
              loadComponent: () => import('./pages/inventory-detail/inventory-detail'),
              providers: [
                {
                  provide: InventoryDetailStrategy,
                  useFactory: () => new InventoryDetailCreateStrategy(),
                },
              ],
            },
            {
              path: 'products/:productId/items',
              loadComponent: () =>
                import('./pages/inventory-item-list/inventory-item-list'),
            },
            {
              path: 'products/:productId/items/:itemId',
              loadComponent: () => import('./pages/inventory-detail/inventory-detail'),
              providers: [
                {
                  provide: InventoryDetailStrategy,
                  useFactory: () => new InventoryDetailEditStrategy(),
                },
              ],
            },
            {
              path: 'search',
              loadComponent: () => import('./pages/product-search/product-search'),
            },
          ],
        },
      ],
      resolve: {
        config: RemoteConfigResolver,
      },
    },
  ];
};
